import React, { useCallback, useMemo, useState, useRef, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  Alert,
} from "react-native";
import { useTranslation } from "react-i18next";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import { sharePayload } from "../../../../uiComponents/ShareUtils";
import CopyIcon from "../../../../../../../../assets/images/copy.svg";
import ReceiptIcon from "../../../../../../../../assets/images/receipt.svg";
import ShareIcon from "../../../../../../../../assets/images/share.ios.svg";
import copyToClipboard from "../../../../../helper/copyToClipBoard";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import AnimatedSuccessIcon from "../../../common/AnimatedSuccessView";
import { capturePosthogEvent } from "../../../../../utils/analytics";

interface MapiHeadToCashierStepProps {
  confirmationCode?: string;
  onFinish?: () => void;
  testID?: string | undefined;
}

const MapiHeadToCashierStep: React.FC<MapiHeadToCashierStepProps> = ({
  confirmationCode: confirmationCodeProp,
  onFinish,
  testID,
}) => {
  const { t } = useTranslation();
  const [sharing, setSharing] = useState(false);

  const pendingSendOption = useSessionStore(
    (s: SessionState) => (s as any).pendingSendOption
  );
  const confirmationNumber = useSessionStore(
    (s: SessionState) => (s as any).confirmationNumber
  );
  const senderFirstName = useSessionStore(
    (s: SessionState) => (s as any).senderFirstName
  );
  const mapiReceiverDetails = useSessionStore(
    (s: SessionState) => (s as any).mapiReceiverDetails
  );

  const referenceNumber = confirmationCodeProp || confirmationNumber || "";

  const receiverName = useMemo(() => {
    if (mapiReceiverDetails && typeof mapiReceiverDetails === "object") {
      const first =
        mapiReceiverDetails["receiver.name.firstName"] ||
        mapiReceiverDetails["receiver.name.givenName"] ||
        "";
      const middle = mapiReceiverDetails["receiver.name.middleName"] || "";
      const last =
        mapiReceiverDetails["receiver.name.lastName"] ||
        mapiReceiverDetails["receiver.name.familyName"] ||
        "";
      return [first, middle, last].filter(Boolean).join(" ");
    }
    return "";
  }, [mapiReceiverDetails]);

  const receiverFirstNameDisplay = useMemo(() => {
    if (mapiReceiverDetails && typeof mapiReceiverDetails === "object") {
      return (
        mapiReceiverDetails["receiver.name.firstName"] ||
        mapiReceiverDetails["receiver.name.givenName"] ||
        ""
      );
    }
    return "";
  }, [mapiReceiverDetails]);

  const option: any = pendingSendOption || null;
  const receiveAmountValue = Number(
    option?.receiveAmount?.amount?.value ?? 0
  );
  const receiveAmountFormatted = useMemo(() => {
    return isFinite(receiveAmountValue)
      ? receiveAmountValue.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "0.00";
  }, [receiveAmountValue]);
  const receiveCurrency =
    option?.receiveAmount?.amount?.currencyCode || "USD";
  const sendCurrency =
    option?.sendAmount?.amount?.currencyCode || "USD";
  const sendAmountValue = Number(option?.sendAmount?.amount?.value ?? 0);
  const feesValue = Number(option?.sendAmount?.fees?.value ?? 0);
  const totalValue = sendAmountValue + feesValue;

  const hasLoggedEvent = useRef(false);
  useEffect(() => {
    if (!hasLoggedEvent.current) {
      hasLoggedEvent.current = true;
      capturePosthogEvent("Mapi Send Flow - Head to Cashier Step", {
        "data-mg-reference-number": referenceNumber,
      });
    }
  }, [referenceNumber]);

  const buildReceiptText = () => {
    return `MoneyGram Receipt\n\nReceiver: ${receiverName}\nAmount: ${receiveAmountFormatted} ${receiveCurrency}\nConfirmation: ${referenceNumber}\nStatus: Staged\n\nThank you for using MoneyGram.`;
  };

  const onShare = useCallback(async () => {
    setSharing(true);
    try {
      const payload = {
        title: "MoneyGram Receipt",
        message: buildReceiptText(),
      };

      const result = await sharePayload(payload);
      if (Platform.OS === "web") {
        if ((result as any).usedWebShare) {
          console.log("Shared via navigator.share", result);
        } else if ((result as any).usedClipboard) {
          Alert.alert(
            "Share not available",
            "Share sheet is not supported in this browser. Receipt text has been copied to clipboard."
          );
        }
      }
    } catch (err: any) {
      console.error("Share error:", err);
      Alert.alert(
        "Unable to share",
        err?.message || "An error occurred while sharing."
      );
    } finally {
      capturePosthogEvent("Mapi Send Flow - Share Receipt", {
        "data-mg-reference-number": referenceNumber,
      });
      setSharing(false);
    }
  }, [receiverName, receiveAmountFormatted, receiveCurrency, referenceNumber]);

  return (
    <View style={styles.container} testID={testID}>
      <View style={{ alignItems: "center" }}>
        <AnimatedSuccessIcon />

        <Text style={styles.title}>{t("HEAD_TO_CASHIER")}</Text>
        <Text style={styles.subtitle}>{t("THANK_YOU_MONEYGRAM")}</Text>

        <Text style={styles.receiverLine}>
          {receiverFirstNameDisplay} {t("WILL_RECEIVE")}
        </Text>
        <Text style={styles.amount}>
          {receiveAmountFormatted}
          <Text style={styles.currencyStyle}> {receiveCurrency}</Text>
        </Text>

        <Text style={[styles.referenceLabel, { marginBottom: 9 }]}>
          {t("STATUS")}{" "}
          <Text style={styles.referenceNumber}>Staged</Text>
        </Text>
      </View>

      <View style={styles.referenceRow}>
        <Text style={styles.referenceLabel}>{t("REFERENCE")}</Text>
        <TouchableOpacity
          style={styles.referenceCopy}
          onPress={() => copyToClipboard(referenceNumber)}
          testID="mapiReferenceNumberCopy"
        >
          <Text style={styles.referenceNumber} testID="mapiConfirmationCode">
            {referenceNumber}
          </Text>
          <CopyIcon width={24} height={24} />
        </TouchableOpacity>
      </View>

      <Text style={styles.instructionTitle}>{t("WHAT_DO_I_SAY")}</Text>
      <Text style={styles.instruction}>{t("LET_THE_CASHIER_KNOW")}</Text>

      <View style={styles.actionsRow}>
        <TouchableOpacity
          style={[styles.actionBox, { marginRight: 12 }]}
          onPress={onShare}
          testID="mapiShareReceiptButton"
        >
          <View style={styles.receiptView}>
            <ShareIcon width={24} height={24} />
          </View>
          <Text style={styles.actionLabel}>{t("SHARE_RECEIPT")}</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.actionBox}
          onPress={onFinish}
          testID="mapiFinishButton"
        >
          <View style={styles.receiptView}>
            <ReceiptIcon width={24} height={24} />
          </View>
          <Text style={styles.actionLabel}>Done</Text>
        </TouchableOpacity>
      </View>

      {sharing && (
        <View style={styles.overlay}>
          <ActivityIndicator size="large" color="#fff" />
          <Text style={styles.overlayText}>{t("PREPARING_SHARE")}</Text>
        </View>
      )}
    </View>
  );
};

export default MapiHeadToCashierStep;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 24,
    paddingTop: 20,
  },
  title: {
    fontSize: 28,
    color: Colors.darkGrey,
    fontWeight: "500",
    fontFamily: "Rubik",
    lineHeight: 34,
    marginBottom: 8,
    marginTop: 24,
  },
  subtitle: {
    fontSize: 16,
    color: Colors.black,
    fontWeight: "400",
    fontFamily: "Rubik",
    marginBottom: 32,
  },
  receiverLine: {
    fontSize: 16,
    color: Colors.titleTextColor,
    fontWeight: "400",
    fontFamily: "Rubik",
    lineHeight: 22,
    marginBottom: 6,
  },
  amount: {
    fontSize: 40,
    color: Colors.titleTextColor,
    fontWeight: "500",
    fontFamily: "Rubik",
    marginBottom: 6,
    lineHeight: 46,
  },
  currencyStyle: {
    fontSize: 24,
    fontFamily: "Rubik",
    fontWeight: "500",
    color: Colors.infoSubValue,
    lineHeight: 30,
  },
  referenceRow: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 36,
    backgroundColor: Colors.buttonTextColor,
    borderRadius: 8,
    paddingVertical: 12,
  },
  referenceLabel: {
    fontSize: 16,
    color: Colors.darkGrey,
    fontWeight: "500",
    fontFamily: "Rubik",
    lineHeight: 24,
    marginRight: 12,
  },
  referenceCopy: {
    flexDirection: "row",
    alignItems: "center",
  },
  referenceNumber: {
    fontSize: 16,
    color: Colors.termsAndConditionsBlue,
    fontWeight: "400",
    fontFamily: "Rubik",
    lineHeight: 26,
    marginRight: 12,
  },
  instructionTitle: {
    fontSize: 20,
    color: Colors.darkGrey,
    lineHeight: 26,
    marginBottom: 8,
    fontFamily: "Rubik",
    fontWeight: "500",
    textAlign: "left",
  },
  instruction: {
    fontSize: 16,
    color: Colors.black,
    marginBottom: 24,
    fontFamily: "Rubik",
    fontWeight: "400",
  },
  actionsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 39,
    width: "100%",
  },
  actionBox: {
    flex: 1,
    padding: 16,
    borderColor: "#ddd",
    backgroundColor: Colors.searchCompanyBackground,
    borderRadius: 16,
  },
  receiptView: {
    width: 40,
    height: 40,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: Colors.secondaryText,
    marginBottom: 12,
    borderRadius: 30,
  },
  actionLabel: {
    fontSize: 16,
    color: Colors.titleTextColor,
    fontFamily: "Rubik",
    fontWeight: "500",
    lineHeight: 24,
  },
  overlay: {
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.45)",
    justifyContent: "center",
    alignItems: "center",
  },
  overlayText: {
    color: "#fff",
    marginTop: 10,
  },
});
